/**
 * HistoryModal — "My orders": the device's past online orders with one-tap
 * reorder. History is local to this browser (no account), capped at 20 orders.
 * Reordering re-maps items onto the live menu, so prices and availability are
 * always current; anything 86'd or removed is reported, not silently added.
 */
import React, { useState } from 'react';
import { useOrdering } from './OrderingContext';
import { buildReorderCart } from './orderHistory';

const cents$ = (c) => `$${((c || 0) / 100).toFixed(2)}`;

function when(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ' · ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export default function HistoryModal() {
  const {
    canOrder, cart, history, wipeHistory, menuCategories,
    historyOpen, setHistoryOpen, openCart,
  } = useOrdering();
  const [msg, setMsg] = useState('');

  if (!canOrder || !historyOpen) return null;

  const close = () => { setMsg(''); setHistoryOpen(false); };

  const reorder = (order) => {
    const { adds, skipped } = buildReorderCart(order, menuCategories);
    adds.forEach(({ cfg, qty }) => cart.addConfigured(cfg, qty));
    if (adds.length) {
      close();
      openCart();
    }
    if (skipped.length) setMsg(`Not available right now: ${skipped.join(', ')}.`);
    else if (!adds.length) setMsg('None of those items are available right now.');
  };

  return (
    <div className="ord-overlay" onClick={close}>
      <div className="ord-modal" onClick={e => e.stopPropagation()} role="dialog" aria-label="My orders">
        <div className="ord-drawer__head">
          <h3>My orders</h3>
          <button className="ord-x" onClick={close} aria-label="Close">✕</button>
        </div>

        <div className="ord-modal__body">
          {msg && <div className="ord-err">⚠ {msg}</div>}

          {history.length === 0 ? (
            <div className="ord-empty"><span>🕘</span><p>No orders on this device yet.</p></div>
          ) : (
            <>
              {history.map((o, i) => (
                <div key={o.orderId || i} className="osc-hist">
                  <div className="osc-hist__top">
                    <span className="osc-hist__when">{when(o.placedAt)}</span>
                    <span className="osc-hist__total">
                      {o.totalCents != null ? cents$(o.totalCents) : ''}
                      {o.payAtStore && <em className="osc-hist__badge">pay at pickup</em>}
                    </span>
                  </div>
                  <div className="osc-hist__items">
                    {o.items.map(it => `${it.qty}× ${it.name}${it.variationName && it.variationName !== 'Regular' ? ` (${it.variationName})` : ''}`).join(', ')}
                  </div>
                  <div className="osc-hist__actions">
                    {o.receiptUrl && (
                      <a className="osc-hist__receipt" href={o.receiptUrl} target="_blank" rel="noopener noreferrer">Receipt</a>
                    )}
                    <button className="ord-btn ord-btn--primary osc-hist__reorder" onClick={() => reorder(o)}>
                      Reorder
                    </button>
                  </div>
                </div>
              ))}
              <button className="osc-hist__clear" onClick={() => { wipeHistory(); setMsg(''); }}>
                Clear order history on this device
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
